import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeUp, CountUp } from "./Reveal";
import { PortraitFallback } from "./PortraitFallback";
import { CategoryChips } from "./CategoryChips";
import { SchoolChips } from "./SchoolChips";
import { EmSwash } from "./EmSwash";
import { NoteEyebrow } from "./NoteEyebrow";
import { CAMPAIGNS, IMAGES } from "../data/content";
import { CATEGORY_BY_SLUG } from "../data/categories";
import { isPrintMode } from "../lib/printMode";
import { ArrowUpRight, Search } from "lucide-react";

const money = (n) => `$${n.toLocaleString()}`;

export const FeaturedCampaigns = () => {
  const [category, setCategory] = useState(null);
  const [school, setSchool] = useState(null);

  // Schools that currently have at least one active (daysLeft > 0) campaign.
  // Sorted alphabetically for stable chip order across renders.
  const activeSchools = useMemo(
    () =>
      Array.from(
        new Set(
          CAMPAIGNS.filter((c) => (c.daysLeft ?? 1) > 0).map((c) => c.school)
        )
      ).sort((a, b) => a.localeCompare(b)),
    []
  );

  const filtered = useMemo(
    () =>
      CAMPAIGNS.filter(
        (c) =>
          (!category || c.category === category) &&
          (!school || c.school === school)
      ),
    [category, school]
  );

  // Contextual counts — each dimension's counts respect the OTHER dimension's
  // current filter. Same behavior Airbnb / Zillow use for their filter chips:
  // the count next to "Housing" tells the donor how many campaigns will
  // appear if they add Housing on top of their current School selection.
  const categoryCounts = useMemo(() => {
    const map = {};
    for (const c of CAMPAIGNS) {
      if (school && c.school !== school) continue;
      map[c.category] = (map[c.category] || 0) + 1;
    }
    return map;
  }, [school]);

  const schoolCounts = useMemo(() => {
    const map = {};
    for (const c of CAMPAIGNS) {
      if (category && c.category !== category) continue;
      map[c.school] = (map[c.school] || 0) + 1;
    }
    return map;
  }, [category]);

  const activeCategoryLabel = category ? CATEGORY_BY_SLUG[category]?.label : null;
  const activeSchoolLabel = school;

  // Empty-state copy adapts to which filters are on. When both filters are
  // set, the message names both. When only one, it names only that one.
  const emptyHeadline = (() => {
    if (category && school) {
      return (
        <>
          No <EmSwash>{activeCategoryLabel}</EmSwash>{" "}
          campaigns at{" "}
          <EmSwash>{activeSchoolLabel}</EmSwash>{" "}
          right now.
        </>
      );
    }
    if (category) {
      return (
        <>
          No campaigns in{" "}
          <EmSwash>{activeCategoryLabel}</EmSwash>{" "}
          right now.
        </>
      );
    }
    if (school) {
      return (
        <>
          No campaigns at{" "}
          <EmSwash>{activeSchoolLabel}</EmSwash>{" "}
          right now.
        </>
      );
    }
    return <>No campaigns right now.</>;
  })();

  const resetFilters = () => {
    setCategory(null);
    setSchool(null);
  };

  return (
    <section id="campaigns" className="mx-auto max-w-7xl px-6 py-32 lg:px-8 lg:py-48">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <FadeUp>
          <NoteEyebrow>sample campaigns</NoteEyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.95] tracking-tight text-ink">
            What a campaign <EmSwash>looks like.</EmSwash>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="max-w-xs text-[13px] text-olive">
            Illustrative profiles built to show how a student page works. These are not real students, and no money is raised here.
          </p>
        </FadeUp>
      </div>

      <FadeUp delay={0.12}>
        <div
          data-testid="sample-campaigns-notice"
          className="mt-8 flex flex-col gap-3 rounded-md border border-cobalt/30 bg-cobalt/[0.05] px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="text-base leading-relaxed text-ink">
            <span className="mr-2 rounded-full bg-cobalt px-2.5 py-0.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-sand">Sample</span>
            The names, photos and stories below are fictional composites. Real, verified campaigns appear at{" "}
            <Link to="/campaigns" className="font-semibold text-cobalt underline underline-offset-2 hover:text-ink">myrichaunt.org/campaigns</Link>{" "}
            once the first cohort is live.
          </p>
        </div>
      </FadeUp>

      {/* Filter tape — schools first, then categories */}
      <FadeUp delay={0.15}>
        <div className="mt-10 space-y-6">
          <div>
            <p className="mb-3 text-[13px] font-medium uppercase tracking-[0.18em] text-olive">
              Browse by school
            </p>
            <SchoolChips schools={activeSchools} selected={school} onSelect={setSchool} counts={schoolCounts} />
          </div>
          <div>
            <p className="mb-3 text-[13px] font-medium uppercase tracking-[0.18em] text-olive">
              Browse by need
            </p>
            <CategoryChips selected={category} onSelect={setCategory} counts={categoryCounts} />
          </div>
        </div>
      </FadeUp>

      {filtered.length > 0 ? (
        <div
          className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
          data-testid="campaigns-grid"
        >
          {filtered.map((c) => {
            const pct = Math.round((c.raised / c.goal) * 100);
            // Preserve stable per-campaign URLs by resolving the ORIGINAL index in CAMPAIGNS.
            const originalIdx = CAMPAIGNS.indexOf(c);
            return (
              <FadeUp key={c.name} delay={0}>
                <Link
                  to={`/demo/campaign/${originalIdx}`}
                  data-testid={`campaign-card-${originalIdx}`}
                  className="group flex h-full flex-col rounded-md border border-border bg-bone transition-shadow hover:shadow-lg"
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-t-md">
                      {IMAGES[c.img] ? (
                        <img
                          src={IMAGES[c.img]}
                          alt={c.name}
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <PortraitFallback
                          name={c.name}
                          school={c.school}
                          gradYear={c.gradYear}
                          variant={c.paletteVariant}
                          nameSize="text-[clamp(3rem,7vw,5.5rem)]"
                          className="transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                      <span className="absolute left-4 top-4 rounded-full bg-sand/90 px-3 py-1 text-[13px] font-semibold text-ink backdrop-blur">
                        {pct}% funded
                      </span>
                      <span className="absolute right-4 top-4 rounded-full bg-cobalt px-2.5 py-1 text-[13px] font-semibold uppercase tracking-[0.18em] text-sand" data-testid={`campaign-card-sample-${originalIdx}`}>
                        Sample
                      </span>
                    </div>
                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="font-serif text-3xl leading-none text-ink">{c.name}</h3>
                    <p className="mt-2 text-[13px] text-olive">{c.school} · {c.major}</p>
                    <p className="mt-5 flex-1 font-serif text-3xl italic leading-snug text-ink">&ldquo;{c.headline}&rdquo;</p>

                    <div className="mt-8">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        {isPrintMode ? (
                          <div className="h-full rounded-full bg-tangerine" style={{ width: `${pct}%` }} />
                        ) : (
                          <motion.div
                            className="h-full rounded-full bg-tangerine"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${pct}%` }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                          />
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[13px]">
                        <span className="font-semibold text-ink">
                          <CountUp to={c.raised} prefix="$" /> raised
                        </span>
                        <span className="text-olive">of {money(c.goal)}</span>
                      </div>
                    </div>

                    <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink underline-offset-4 group-hover:underline">
                      View campaign
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      ) : (
        <FadeUp>
          <div
            className="mt-10 flex flex-col items-center justify-center rounded-md border border-dashed border-ink/20 bg-bone px-8 py-16 text-center"
            data-testid="campaigns-empty"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive/10 text-olive">
              <Search className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-4 font-serif text-3xl text-ink">{emptyHeadline}</h3>
            <p className="mt-2 max-w-md text-[13px] text-olive">
              We&rsquo;re still adding new students. Check back soon, or view all campaigns to find someone to back today.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              data-testid="campaigns-empty-reset"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest"
            >
              View all campaigns
            </button>
          </div>
        </FadeUp>
      )}
    </section>
  );
};
